import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { parseBookingPDF } from '../../utils/pdfParser';
import { guestNotesAPI } from '../../services/api';
import { format } from 'date-fns';

const { FiSave, FiX, FiCopy, FiMail, FiUpload, FiLoader, FiAlertCircle, FiRefreshCw, FiKey, FiCheck, FiLock, FiFileText, FiPlus, FiTrash2, FiEdit2, FiTag } = FiIcons;

const GuestPageForm = ({ guestPage, onClose }) => {
  const { properties, createGuestPage, updateGuestPage } = useData();
  const fileInputRef = useRef(null);
  
  // Helper to extract last 4 digits
  const getLast4 = (phoneStr) => {
    if (!phoneStr) return '';
    const clean = phoneStr.replace(/\D/g, '');
    return clean.length >= 4 ? clean.slice(-4) : clean;
  };

  // Helper to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    if (!date) return '';
    // If already in YYYY-MM-DD format, return as is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Try to parse and format
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const [formData, setFormData] = useState({
    propertyId: guestPage?.propertyId || '',
    guestName: guestPage?.guestName || '',
    guestEmail: guestPage?.guestEmail || '',
    phone: guestPage?.phone || '',
    checkInDate: formatDateForInput(guestPage?.checkInDate) || '',
    checkOutDate: formatDateForInput(guestPage?.checkOutDate) || '',
    doorCode: guestPage?.doorCode || '',
    reservationId: guestPage?.reservationId || '',
    specialInstructions: guestPage?.specialInstructions || '',
    lastFourDigits: guestPage?.lastFourDigits || ''
  });

  // Update form data when guestPage prop changes (for editing)
  useEffect(() => {
    if (guestPage) {
      setFormData({
        propertyId: guestPage.propertyId || '',
        guestName: guestPage.guestName || '',
        guestEmail: guestPage.guestEmail || '',
        phone: guestPage.phone || '',
        checkInDate: formatDateForInput(guestPage.checkInDate) || '',
        checkOutDate: formatDateForInput(guestPage.checkOutDate) || '',
        doorCode: guestPage.doorCode || '',
        reservationId: guestPage.reservationId || '',
        specialInstructions: guestPage.specialInstructions || '',
        lastFourDigits: guestPage.lastFourDigits || ''
      });
    }
  }, [guestPage]);

  // Load notes when editing a guest page
  useEffect(() => {
    if (guestPage?.id) {
      loadNotes();
    } else {
      setNotes([]);
    }
  }, [guestPage?.id]);

  const loadNotes = async () => {
    if (!guestPage?.id) return;
    try {
      setIsLoadingNotes(true);
      const result = await guestNotesAPI.getNotes(guestPage.id);
      if (result.success) {
        setNotes(result.notes || []);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !guestPage?.id) return;
    
    try {
      const tags = newNoteTags.split(',').map(t => t.trim()).filter(t => t);
      const result = await guestNotesAPI.createNote(guestPage.id, newNote.trim(), tags);
      if (result.success) {
        setNotes([result.note, ...notes]);
        setNewNote('');
        setNewNoteTags('');
        setShowAddNote(false);
        toast.success('Note added successfully');
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await guestNotesAPI.deleteNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleUpdateNote = async (noteId, noteText, tags) => {
    try {
      const tagArray = typeof tags === 'string' ? tags.split(',').map(t => t.trim()).filter(t => t) : tags;
      const result = await guestNotesAPI.updateNote(noteId, noteText, tagArray);
      if (result.success) {
        setNotes(notes.map(n => n.id === noteId ? result.note : n));
        setEditingNoteId(null);
        toast.success('Note updated');
      }
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const [createdPage, setCreatedPage] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [isPasswordManuallyEdited, setIsPasswordManuallyEdited] = useState(!!guestPage);
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteTags, setNewNoteTags] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [editingNoteTags, setEditingNoteTags] = useState('');

  // Helper to generate random 4-digit code
  const generateRandomCode = () => Math.floor(1000 + Math.random() * 9000).toString();

  const handleGenerateDoorCode = () => {
    setFormData(prev => ({ ...prev, doorCode: generateRandomCode() }));
    toast.success('New door code generated');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updates = { [name]: value };
      
      // Auto-update password if phone changes and password wasn't manually edited
      if (name === 'phone' && !isPasswordManuallyEdited) {
        updates.lastFourDigits = getLast4(value);
      }
      
      return { ...prev, ...updates };
    });

    if (name === 'lastFourDigits') {
      setIsPasswordManuallyEdited(true);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    setIsParsing(true);
    setParseError(null);

    try {
      const extractedInfo = await parseBookingPDF(file);
      
      setFormData(prev => {
        // Calculate values inside the setter to access 'prev' safely
        const finalDoorCode = extractedInfo.doorCode || prev.doorCode || generateRandomCode();
        const finalPhone = extractedInfo.phone || prev.phone;
        
        return {
          ...prev,
          guestName: extractedInfo.guestName || prev.guestName,
          guestEmail: extractedInfo.guestEmail || prev.guestEmail,
          phone: finalPhone,
          reservationId: extractedInfo.reservationId || prev.reservationId,
          checkInDate: extractedInfo.checkInDate || prev.checkInDate,
          checkOutDate: extractedInfo.checkOutDate || prev.checkOutDate,
          doorCode: finalDoorCode,
          // Reset manual edit flag so this auto-updates based on new phone
          lastFourDigits: getLast4(finalPhone) || prev.lastFourDigits
        };
      });
      
      // Reset manual flag to allow auto-update from the new phone number
      setIsPasswordManuallyEdited(false);

      // Show what was found
      const foundFields = Object.entries(extractedInfo)
        .filter(([_, val]) => val && val.length > 0)
        .map(([key]) => key);
        
      if (foundFields.length > 0) {
        toast.success(`Parsed: ${foundFields.join(', ')}`);
        if (!extractedInfo.doorCode) toast.success('Auto-generated Door Code');
      } else {
        toast('PDF parsed but no specific fields identified.', { icon: '⚠️' });
      }

    } catch (error) {
      console.error(error);
      setParseError('Failed to read the PDF.');
      toast.error('Could not parse PDF file');
    } finally {
      setIsParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure lastFourDigits is set (fallback to phone last 4 if empty)
    const finalData = {
      ...formData,
      lastFourDigits: formData.lastFourDigits || getLast4(formData.phone)
    };

    try {
      if (guestPage) {
        await updateGuestPage(guestPage.id, finalData);
        toast.success('Guest page updated successfully!');
        onClose();
      } else {
        const newPage = await createGuestPage(finalData);
        setCreatedPage(newPage);
        toast.success('Guest page created successfully!');
      }
    } catch (error) {
      toast.error('Failed to save guest page.');
    }
  };

  const copyPortalLink = () => {
    if (createdPage) {
      const link = `${window.location.origin}#/portal/${createdPage.slug}`;
      navigator.clipboard.writeText(link);
      toast.success('Portal link copied!');
    }
  };

  const sendEmail = () => {
    if (createdPage && formData.guestEmail) {
      const link = `${window.location.origin}#/portal/${createdPage.slug}`;
      const property = properties.find(p => p.id === formData.propertyId);
      const subject = `Your Stay Information - ${property?.name}`;
      const body = `Hi ${formData.guestName},

Your guest portal is ready! Access your stay information using the link below:

${link}

Access Password: ${createdPage.lastFourDigits}

Best regards,
${property?.name} Team`;
      
      const mailtoLink = `mailto:${formData.guestEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink);
    }
  };

  if (createdPage) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
      >
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiSave} className="text-green-600 text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Guest Page Created!</h2>
          <p className="text-gray-600 mb-6">
            The guest portal has been created for {formData.guestName}
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="mb-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Portal Link</p>
              <div className="flex bg-white border border-gray-200 rounded p-2">
                <p className="font-mono text-sm break-all flex-1">
                  {window.location.origin}#/portal/{createdPage.slug}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
               <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Access Password</p>
                <div className="bg-white border border-gray-200 rounded p-2 font-mono text-sm font-bold flex items-center justify-between">
                  <span>{createdPage.lastFourDigits}</span>
                  <SafeIcon icon={FiLock} className="text-gray-400 text-xs" />
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Door Code</p>
                <div className="bg-white border border-gray-200 rounded p-2 font-mono text-sm font-bold">
                  {createdPage.doorCode}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center space-x-3">
            <button
              onClick={copyPortalLink}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiCopy} />
              <span>Copy Link</span>
            </button>
            {formData.guestEmail && (
              <button
                onClick={sendEmail}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <SafeIcon icon={FiMail} />
                <span>Send Email</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Done
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {guestPage ? 'Edit Guest Page' : 'Create New Guest Page'}
        </h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </div>

      <div className="mb-8">
        <div className="bg-blue-50 border-2 border-blue-100 border-dashed rounded-xl p-6 text-center hover:bg-blue-100 transition-colors duration-200">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="application/pdf"
            className="hidden"
            id="pdf-upload"
          />
          <label
            htmlFor="pdf-upload"
            className="cursor-pointer flex flex-col items-center justify-center"
          >
            {isParsing ? (
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="mb-2"
              >
                <SafeIcon icon={FiLoader} className="text-3xl text-blue-600" />
              </motion.div>
            ) : (
              <SafeIcon icon={FiUpload} className="text-3xl text-blue-500 mb-2" />
            )}
            <span className="text-sm font-medium text-blue-900">
              {isParsing ? 'Parsing PDF...' : 'Auto-fill from Booking PDF'}
            </span>
            <span className="text-xs text-blue-600 mt-1">
              Upload a booking confirmation (Airbnb, VRBO, etc.)
            </span>
          </label>
        </div>
        
        <AnimatePresence>
          {parseError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2 text-sm text-red-600 flex items-center"
            >
              <SafeIcon icon={FiAlertCircle} className="mr-1" />
              {parseError}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property *
          </label>
          <select
            name="propertyId"
            value={formData.propertyId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select a property</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </select>
        </div>

        {/* Guest Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Guest Name *
            </label>
            <input
              type="text"
              name="guestName"
              value={formData.guestName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Guest Email
            </label>
            <input
              type="email"
              name="guestEmail"
              value={formData.guestEmail}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Phone and Access Password */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="(555) 123-4567"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Access Password *
            </label>
            <div className="relative">
              <input
                type="text"
                name="lastFourDigits"
                value={formData.lastFourDigits}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="1234"
                required
              />
              <div className="absolute right-2 top-2 text-xs text-gray-400">
                <SafeIcon icon={FiKey} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Auto from phone last 4
            </p>
          </div>
        </div>

        {/* Check-in and Check-out Dates - Same Line */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Check-in Date *
            </label>
            <input
              type="date"
              name="checkInDate"
              value={formData.checkInDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Check-out Date *
            </label>
            <input
              type="date"
              name="checkOutDate"
              value={formData.checkOutDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        {/* Reservation ID and Door Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Reservation ID
            </label>
            <input
              type="text"
              name="reservationId"
              value={formData.reservationId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Door Code
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                name="doorCode"
                value={formData.doorCode}
                onChange={handleChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="1234"
              />
              <button
                type="button"
                onClick={handleGenerateDoorCode}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center space-x-1 text-sm"
                title="Generate random code"
              >
                <SafeIcon icon={FiRefreshCw} className="text-sm" />
                <span>Generate</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Shown to guest in portal
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Door Code
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              name="doorCode"
              value={formData.doorCode}
              onChange={handleChange}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              placeholder="1234"
            />
            <button
              type="button"
              onClick={handleGenerateDoorCode}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              title="Generate Random Code"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span className="text-sm">Generate</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This code will be shown to the guest in their portal.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Special Instructions
          </label>
          <textarea
            name="specialInstructions"
            value={formData.specialInstructions}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Any special instructions for this guest..."
          />
        </div>

        {/* Guest Notes Section - Only show when editing existing guest */}
        {guestPage?.id && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiFileText} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Guest Notes</h3>
                <span className="text-sm text-gray-500">({notes.length})</span>
              </div>
              {!showAddNote && (
                <button
                  type="button"
                  onClick={() => setShowAddNote(true)}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiPlus} />
                  <span>Add Note</span>
                </button>
              )}
            </div>

            {/* Add Note Form */}
            {showAddNote && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this guest..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                />
                <input
                  type="text"
                  value={newNoteTags}
                  onChange={(e) => setNewNoteTags(e.target.value)}
                  placeholder="Tags (comma-separated, e.g., VIP, repeat, special_needs)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2 text-sm"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddNote(false);
                      setNewNote('');
                      setNewNoteTags('');
                    }}
                    className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Save Note
                  </button>
                </div>
              </div>
            )}

            {/* Notes List */}
            {isLoadingNotes ? (
              <div className="text-center py-4 text-gray-500">Loading notes...</div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg">
                <SafeIcon icon={FiFileText} className="mx-auto text-3xl mb-2 opacity-50" />
                <p className="text-sm">No notes yet. Add a note to track important information about this guest.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    {editingNoteId === note.id ? (
                      <div>
                        <textarea
                          value={editingNoteText || note.note}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                        />
                        <input
                          type="text"
                          value={editingNoteTags || (note.tags?.join(', ') || '')}
                          onChange={(e) => setEditingNoteTags(e.target.value)}
                          placeholder="Tags (comma-separated)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2 text-sm"
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingNoteText('');
                              setEditingNoteTags('');
                            }}
                            className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdateNote(note.id, editingNoteText || note.note, editingNoteTags || (note.tags?.join(', ') || ''))}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-gray-900 flex-1">{note.note}</p>
                          <div className="flex space-x-2 ml-4">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingNoteId(note.id);
                                setEditingNoteText(note.note);
                                setEditingNoteTags(note.tags?.join(', ') || '');
                              }}
                              className="text-gray-400 hover:text-blue-600"
                            >
                              <SafeIcon icon={FiEdit2} className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <SafeIcon icon={FiTrash2} className="text-sm" />
                            </button>
                          </div>
                        </div>
                        {note.tags && note.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {note.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                <SafeIcon icon={FiTag} className="mr-1 text-xs" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiSave} />
            <span>{guestPage ? 'Update' : 'Create'} Guest Page</span>
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default GuestPageForm;